import { useEffect, useCallback, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

interface UseBeforeUnloadOptions {
  /**
   * 離脱防止を有効にするかどうか
   * @default true
   */
  enabled?: boolean;
  /**
   * 離脱を許可する条件
   * 例: 保存・ダウンロード処理中など
   */
  shouldBypass?: () => boolean;
}

/**
 * ページ離脱時に確認ダイアログを表示するカスタムフック
 *
 * このフックは、ユーザーが誤操作でページを離脱することを防ぎます。
 * 以下の2つの方法でページ離脱を検知します:
 *
 * 1. **ブラウザ/タブを閉じる、リロードする場合**: beforeunloadイベント
 *    - ブラウザのデフォルト確認ダイアログが表示されます
 *    - メッセージのカスタマイズは最近のブラウザではセキュリティ上の理由から無視されます
 *
 * 2. **React Routerで別ページに遷移する場合**: useBlocker API
 *    - カスタムメッセージで確認ダイアログを表示できます
 *    - ナビゲーションを続行するか、キャンセルするかを選択できます
 *
 * @param options - フックのオプション
 * @param options.enabled - 離脱防止を有効にするかどうか (デフォルト: true)
 * @param options.shouldBypass - 離脱を許可する条件を返す関数 (例: 保存処理中)
 *
 * @example
 * ```tsx
 * function BCPCreatePage() {
 *   const [isSaving, setIsSaving] = useState(false);
 *
 *   // 保存・ダウンロード処理中は確認をスキップ
 *   useBeforeUnload({
 *     enabled: true,
 *     shouldBypass: () => isSaving,
 *   });
 *
 *   const handleSave = async () => {
 *     setIsSaving(true);
 *     try {
 *       await saveBCP();
 *     } finally {
 *       setIsSaving(false);
 *     }
 *   };
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useBeforeUnload({
  enabled = true,
  shouldBypass,
}: UseBeforeUnloadOptions = {}) {
  // shouldBypass関数の参照を保持 (再レンダリング時の不要な更新を防ぐ)
  const shouldBypassRef = useRef(shouldBypass);
  useEffect(() => {
    shouldBypassRef.current = shouldBypass;
  }, [shouldBypass]);

  // beforeunloadイベントハンドラ
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      // 無効化されている場合、またはバイパス条件が満たされている場合はスキップ
      if (!enabled || (shouldBypassRef.current && shouldBypassRef.current())) {
        return;
      }

      // 標準的な方法で確認ダイアログを表示
      event.preventDefault();
      // 最近のブラウザでは空文字列を設定することが推奨される
      event.returnValue = '';
    },
    [enabled]
  );

  // beforeunloadイベントの登録/解除
  useEffect(() => {
    if (!enabled) {
      return;
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, handleBeforeUnload]);

  // React Routerのナビゲーションをブロック
  const blocker = useBlocker(
    useCallback(() => {
      // 無効化されている場合、またはバイパス条件が満たされている場合はブロックしない
      if (!enabled || (shouldBypassRef.current && shouldBypassRef.current())) {
        return false;
      }
      return true;
    }, [enabled])
  );

  // ナビゲーションがブロックされた場合の確認ダイアログ
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const shouldProceed = window.confirm(
        'このページを離れますか？入力した内容が失われる可能性があります。'
      );

      if (shouldProceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);
}
